import { SENTENCE_SWATCHES, WORD_SWATCHES, swatch, type HighlightMode } from './highlightStyles';
/**
 * Word Highlighter - Highlights current word during playback
 *
 * Two rendering strategies:
 * 1. CSS Highlight API (Chrome 105+) — paints highlights natively via
 *    ::highlight() pseudo-elements. No DOM mutation, works across element
 *    boundaries (Wikipedia <a>, <sup>), scrolls naturally.
 * 2. DOM wrapping fallback — surroundContents() for older browsers.
 *
 * Word finding strategy:
 * Pre-indexes every word in the sentence's text nodes. Each indexed word
 * is guaranteed within a single text node, so cross-element issues vanish.
 * Matches by word text + position proximity rather than fragile char offsets.
 */

import type { WordHighlightInfo } from '../core/StreamingPlayer';

import { speakMathIn } from '../utils/mathSpeech';
import { isHiddenElement } from '../utils/textExtractor';

interface SentenceLocation {
  sentence: string;
  textNodes: { node: Text; offset: number; length: number }[];
  totalLength: number;
  /** Contains a formula: character positions don't line up with the spoken text */
  hasMath?: boolean;
}

/** Formula containers: MathML and the MathJax / KaTeX renderings */
const MATH_SELECTOR = 'math, mjx-container, .MathJax, .MathJax_CHTML, .MathJax_SVG, .katex';

/** A content text node — or a whole formula, with its spoken text */
interface NodeEntry {
  /** null only for a formula with no text of its own (MathJax CHTML draws glyphs with CSS) */
  node: Text | null;
  text: string;
  /** Formula: the visible text nodes to highlight */
  math?: Text[];
}

type MappedNode = NodeEntry & { startInConcat: number };

/** Text nodes a reader sees in a formula (not the hidden MathML copy, TeX annotation or MathJax helper) */
function visibleMathText(el: Element): Text[] {
  const out: Text[] = [];
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let n: Node | null;
  while ((n = walker.nextNode())) {
    const p = n.parentElement;
    if (!p || !n.textContent?.trim()) continue;
    if (p.closest('annotation, annotation-xml, .katex-mathml, mjx-assistive-mml, .MJX_Assistive_MathML, script')) continue;
    out.push(n as Text);
  }
  return out;
}

interface IndexedWord {
  text: string;
  node: Text;
  startOffset: number;
  endOffset: number;
}

export class WordHighlighter {
  private autoScroll: boolean;
  private contentRoots: Element[] = [];
  private lastSentenceText: string = '';
  private currentSentence: SentenceLocation | null = null;
  private currentWords: IndexedWord[] = [];
  private useCSS: boolean;

  // DOM fallback state (only when useCSS is false)
  private highlightEl: HTMLElement | null = null;
  private sentenceHighlightEls: HTMLElement[] = [];

  private mode: HighlightMode = 'wordsent';

  /** Apply the visitor's highlight settings (mode + colors) */
  setStyle(opts: { mode?: HighlightMode; word?: string; sentence?: string }): void {
    if (opts.mode) this.mode = opts.mode;
    const root = this.doc.documentElement.style;
    const w = swatch(WORD_SWATCHES, opts.word);
    const t = swatch(SENTENCE_SWATCHES, opts.sentence);
    root.setProperty('--rv-hl-word-bg', w.bg);
    root.setProperty('--rv-hl-word-fg', w.fg === 'inherit' ? 'currentColor' : w.fg);
    root.setProperty('--rv-hl-word-deco', w.underline ? 'underline 3px' : 'none');
    root.setProperty('--rv-hl-sent-bg', t.bg);
    root.setProperty('--rv-hl-sent-fg', t.fg === 'inherit' ? 'currentColor' : t.fg);
    root.setProperty('--rv-hl-sent-deco', t.underline ? 'underline 2px' : 'none');
    if (this.mode === 'none') this.clearAll();
  }

  /** The document this highlighter paints in: the page, or a same-origin iframe's document */
  private doc: Document;
  private win: Window & typeof globalThis;

  constructor(autoScroll: boolean = true, contentRoots?: Element[], doc: Document = document) {
    this.doc = doc;
    this.win = (doc.defaultView || window) as Window & typeof globalThis;
    this.autoScroll = autoScroll;
    this.contentRoots = contentRoots || [];
    this.useCSS = this.detectHighlightAPI();
    this.injectStyles();
  }

  setContentRoots(roots: Element[]): void {
    this.contentRoots = roots;
  }

  // ---------------------------------------------------------------------------
  // Feature detection
  // ---------------------------------------------------------------------------

  private detectHighlightAPI(): boolean {
    try {
      if (!this.win.CSS || !('highlights' in this.win.CSS)) return false;
      if (typeof this.win.Highlight === 'undefined') return false;
      // Smoke test
      const r = this.doc.createRange();
      const h = new this.win.Highlight(r);
      this.win.CSS.highlights.set('__rv_test', h);
      this.win.CSS.highlights.delete('__rv_test');
      return true;
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------

  private injectStyles(): void {
    if (this.doc.getElementById('rv-highlight-styles')) return;
    const style = this.doc.createElement('style');
    style.id = 'rv-highlight-styles';

    style.textContent = this.useCSS
      ? ` ::highlight(rv-word) { background-color: var(--rv-hl-word-bg, #1a3a6b); color: var(--rv-hl-word-fg, #fff); text-decoration: var(--rv-hl-word-deco, none); } ::highlight(rv-sentence) { background-color: var(--rv-hl-sent-bg, #fff176); color: var(--rv-hl-sent-fg, #1a1a1a); text-decoration: var(--rv-hl-sent-deco, none); } `
      : ` .rv-word-highlight { background: var(--rv-hl-word-bg, #1a3a6b); color: var(--rv-hl-word-fg, #fff) !important; text-decoration: var(--rv-hl-word-deco, none); padding: 2px 4px; margin: 0 -4px; border-radius: 3px; box-decoration-break: clone; -webkit-box-decoration-break: clone; } .rv-sentence-highlight { background: var(--rv-hl-sent-bg, #fff176); color: var(--rv-hl-sent-fg, #000) !important; text-decoration: var(--rv-hl-sent-deco, none); border-radius: 2px; box-decoration-break: clone; -webkit-box-decoration-break: clone; } `;

    this.doc.head.appendChild(style);
  }

  // ---------------------------------------------------------------------------
  // Main entry point
  // ---------------------------------------------------------------------------

  /**
   * Highlight a whole sentence with no word-level timing (non-English voices,
   * browser speech). Scrolls it into view like word highlighting does.
   */
  highlightSentenceOnly(sentence: string): boolean {
    if (this.mode === 'none') return false;
    this.clearWordHighlight();
    this.clearSentenceHighlight();
    this.lastSentenceText = sentence;
    this.currentSentence = this.findSentence(sentence);
    this.currentWords = [];
    if (!this.currentSentence) return false;
    this.applySentenceHighlight(this.currentSentence);
    const first = this.currentSentence.textNodes[0];
    if (first) {
      try {
        const r = this.doc.createRange();
        r.setStart(first.node, first.offset);
        r.setEnd(first.node, first.offset + first.length);
        this.scrollToRange(r);
      } catch { /* ignore */ }
    }
    return true;
  }

  /** Highlight a word; returns whether its sentence was found in this document */
  highlightWord(info: WordHighlightInfo): boolean {
    const isNewSentence = info.sentence !== this.lastSentenceText;

    // New sentence — resolve location once
    if (isNewSentence) {
      this.clearWordHighlight();
      this.clearSentenceHighlight();
      this.lastSentenceText = info.sentence;
      this.currentSentence = this.findSentence(info.sentence);
      this.currentWords = this.currentSentence
        ? this.indexWords(this.currentSentence)
        : [];

      if (this.currentSentence && (this.mode === 'wordsent' || this.mode === 'sent')) {
        this.applySentenceHighlight(this.currentSentence);
      }
    }

    if (this.useCSS) {
      // CSS mode: no DOM mutation, sentence location stays cached
      this.clearWordHighlight();
    } else {
      // DOM mode: previous highlight mutated the DOM — must re-resolve
      this.clearWordHighlight();
      this.currentSentence = this.findSentence(info.sentence);
      this.currentWords = this.currentSentence
        ? this.indexWords(this.currentSentence)
        : [];
    }

    if (this.mode === 'sent' || this.mode === 'none') {
      // Keep following along (auto-scroll) without a word highlight
      if (this.mode === 'sent' && this.currentSentence && isNewSentence) {
        const first = this.currentSentence.textNodes[0];
        if (first) {
          try {
            const r = this.doc.createRange();
            r.setStart(first.node, first.offset);
            r.setEnd(first.node, first.offset + first.length);
            this.scrollToRange(r);
          } catch { /* ignore */ }
        }
      }
      return this.currentSentence !== null;
    }
    if (!this.currentSentence || this.currentWords.length === 0) return this.currentSentence !== null;

    // Find the word range
    const range = this.findWordRange(info);
    if (!range) return true;

    // Render
    if (this.useCSS) {
      this.win.CSS.highlights.set('rv-word', new this.win.Highlight(range));
      this.scrollToRange(range);
    } else {
      this.applyDOMWordHighlight(range);
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // Word finding — word-index matching (robust) + char-position fallback
  // ---------------------------------------------------------------------------

  /**
   * Pre-index every word in the sentence's text nodes.
   * Each word is guaranteed within a single text node.
   */
  private indexWords(location: SentenceLocation): IndexedWord[] {
    const words: IndexedWord[] = [];
    for (const tn of location.textNodes) {
      const text = tn.node.textContent!.substring(tn.offset, tn.offset + tn.length);
      const pattern = /\S+/g;
      let m: RegExpExecArray | null;
      while ((m = pattern.exec(text)) !== null) {
        words.push({
          text: m[0],
          node: tn.node,
          startOffset: tn.offset + m.index,
          endOffset: tn.offset + m.index + m[0].length,
        });
      }
    }
    return words;
  }

  /**
   * Find a Range for the target word.
   * Primary: match by word text + position proximity in indexed words.
   * Fallback: character position mapping (accurate for Polly speech marks).
   */
  private findWordRange(info: WordHighlightInfo): Range | null {
    // Primary: word text matching against indexed words
    const match = this.matchWordByText(info.word, info.charStart);
    if (match) return match;

    // Fallback: exact char-position mapping (Polly speech marks)
    if (this.currentSentence && !this.currentSentence.hasMath) {
      return this.createRangeForCharPos(this.currentSentence, info.charStart, info.charEnd);
    }
    return null;
  }

  /**
   * Find the best matching indexed word by text + position proximity.
   * Strips punctuation for fuzzy matching (e.g. "world." matches "world").
   */
  private matchWordByText(word: string, hintCharStart: number): Range | null {
    if (this.currentWords.length === 0) return null;

    const strip = (s: string) => s.replace(/[^\w'-]/g, '').toLowerCase();
    const target = strip(word);
    if (!target) return null;

    let best: IndexedWord | null = null;
    let bestDist = Infinity;
    let charPos = 0;

    for (const w of this.currentWords) {
      const wStripped = strip(w.text);
      if (wStripped === target || w.text === word) {
        const dist = Math.abs(charPos - hintCharStart);
        if (dist < bestDist) {
          best = w;
          bestDist = dist;
        }
      }
      charPos += w.text.length + 1; // +1 for whitespace gap
    }

    if (!best) return null;

    try {
      const range = this.doc.createRange();
      range.setStart(best.node, best.startOffset);
      range.setEnd(best.node, best.endOffset);
      return range;
    } catch {
      return null;
    }
  }

  /**
   * Exact character-position mapping (best for Polly speech marks).
   */
  private createRangeForCharPos(
    location: SentenceLocation,
    charStart: number,
    charEnd: number
  ): Range | null {
    const range = this.doc.createRange();
    let charsSeen = 0;
    let startSet = false;
    let endSet = false;

    for (const tn of location.textNodes) {
      const nodeCharStart = charsSeen;
      const nodeCharEnd = charsSeen + tn.length;

      if (!startSet && charStart >= nodeCharStart && charStart < nodeCharEnd) {
        range.setStart(tn.node, tn.offset + (charStart - nodeCharStart));
        startSet = true;
      }

      if (!endSet && charEnd > nodeCharStart && charEnd <= nodeCharEnd) {
        range.setEnd(tn.node, tn.offset + (charEnd - nodeCharStart));
        endSet = true;
        break;
      }

      charsSeen += tn.length;
    }

    if (!startSet || !endSet) return null;
    return range;
  }

  // ---------------------------------------------------------------------------
  // Sentence finding
  // ---------------------------------------------------------------------------

  /**
   * Find sentence in DOM — tries exact match, whitespace-collapsed, then fuzzy.
   */
  private findSentence(sentence: string): SentenceLocation | null {
    const textNodes = this.collectTextNodes();
    const { concatenated, nodeMap } = this.buildNodeMap(textNodes);
    const normalized = sentence.trim();

    // 1. Exact match
    let idx = concatenated.indexOf(normalized);
    if (idx !== -1) return this.buildLocation(nodeMap, idx, normalized.length);

    // 2. Whitespace-collapsed
    const collConcat = concatenated.replace(/\s+/g, ' ');
    const collSent = normalized.replace(/\s+/g, ' ');
    idx = collConcat.indexOf(collSent);
    if (idx !== -1) {
      return this.buildLocationFromCollapsed(nodeMap, idx, collSent.length, collConcat, concatenated);
    }

    // 3. Strip footnote annotations ([a], [1], [citation needed])
    const stripFn = (s: string) => s.replace(/\[[\w\s]+\]/g, '').replace(/\s+/g, ' ').trim();
    const strSent = stripFn(normalized);
    const strConcat = stripFn(concatenated);
    idx = strConcat.indexOf(strSent);
    if (idx !== -1) {
      return this.buildLocationFromStripped(nodeMap, concatenated, idx, strSent.length);
    }

    // 4. Prefix match (first 40+ chars)
    const prefix = strSent.substring(0, 40);
    if (prefix.length >= 15) {
      idx = strConcat.indexOf(prefix);
      if (idx !== -1) {
        return this.buildLocationFromStripped(nodeMap, concatenated, idx, strSent.length);
      }
    }

    // 5. Letters and digits only — tolerates the sentence breaks the text extractor
    //    inserts between blocks (e.g. "Heading. Paragraph" vs DOM "HeadingParagraph")
    const page = alnumIndex(concatenated);
    const target = alnumIndex(normalized).text;
    if (target.length >= 3) {
      const j = page.text.indexOf(target);
      if (j !== -1) {
        const start = page.map[j];
        const end = page.map[j + target.length - 1] + 1;
        return this.buildLocation(nodeMap, start, end - start);
      }
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Sentence highlighting
  // ---------------------------------------------------------------------------

  private applySentenceHighlight(location: SentenceLocation): void {
    if (this.useCSS) {
      const ranges: Range[] = [];
      for (const tn of location.textNodes) {
        try {
          const r = this.doc.createRange();
          r.setStart(tn.node, tn.offset);
          r.setEnd(tn.node, tn.offset + tn.length);
          ranges.push(r);
        } catch { /* skip */ }
      }
      if (ranges.length > 0) {
        this.win.CSS.highlights.set('rv-sentence', new this.win.Highlight(...ranges));
      }
    } else {
      // DOM wrapping per text node
      for (const tn of location.textNodes) {
        try {
          const r = this.doc.createRange();
          r.setStart(tn.node, tn.offset);
          r.setEnd(tn.node, tn.offset + tn.length);
          const span = this.doc.createElement('span');
          span.className = 'rv-sentence-highlight';
          r.surroundContents(span);
          this.sentenceHighlightEls.push(span);
        } catch { /* skip */ }
      }
    }
  }

  private clearSentenceHighlight(): void {
    if (this.useCSS) {
      this.win.CSS.highlights?.delete('rv-sentence');
    } else {
      for (const el of this.sentenceHighlightEls) {
        if (el.parentNode) {
          const parent = el.parentNode;
          const text = this.doc.createTextNode(el.textContent || '');
          parent.replaceChild(text, el);
          parent.normalize();
        }
      }
      this.sentenceHighlightEls = [];
    }
  }

  // ---------------------------------------------------------------------------
  // Word highlight rendering
  // ---------------------------------------------------------------------------

  private applyDOMWordHighlight(range: Range): void {
    try {
      const span = this.doc.createElement('span');
      span.className = 'rv-word-highlight';
      range.surroundContents(span);
      this.highlightEl = span;
      if (this.autoScroll) this.scrollIntoViewIfNeeded(span);
    } catch {
      // surroundContents failed — shouldn't happen with indexed words
      // but handle gracefully
    }
  }

  private clearWordHighlight(): void {
    if (this.useCSS) {
      this.win.CSS.highlights?.delete('rv-word');
    } else {
      if (this.highlightEl && this.highlightEl.parentNode) {
        const parent = this.highlightEl.parentNode;
        const text = this.doc.createTextNode(this.highlightEl.textContent || '');
        parent.replaceChild(text, this.highlightEl);
        parent.normalize();
      }
      this.highlightEl = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Scroll
  // ---------------------------------------------------------------------------

  private scrollToRange(range: Range): void {
    if (!this.autoScroll) return;
    const rect = range.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > this.win.innerHeight) {
      const el = range.startContainer.parentElement;
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private scrollIntoViewIfNeeded(el: HTMLElement): void {
    const rect = el.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > this.win.innerHeight) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // ---------------------------------------------------------------------------
  // DOM text-node collection & mapping
  // ---------------------------------------------------------------------------

  /**
   * Text nodes of the content, in order. A formula (MathML, MathJax, KaTeX) is one
   * entry whose text is its spoken form — what the extractor sent for audio — so
   * sentences containing math can still be found.
   */
  private collectTextNodes(): NodeEntry[] {
    const searchRoots = this.contentRoots.length > 0 ? this.contentRoots : [this.doc.body];
    const textNodes: NodeEntry[] = [];

    for (const root of searchRoots) {
      const walker = this.doc.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
        acceptNode: (node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as Element;
            // Hidden steps/tabs/slides aren't read, so don't match words inside them
            if (isHiddenElement(el)) return NodeFilter.FILTER_REJECT;
            // Outermost formula container: one entry, then its text nodes are skipped below
            return el.matches(MATH_SELECTOR) && !el.parentElement?.closest(MATH_SELECTOR)
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_SKIP;
          }
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          const tag = parent.tagName;
          if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'VIDEO', 'AUDIO', 'CANVAS', 'OBJECT', 'TEMPLATE'].includes(tag)) {
            return NodeFilter.FILTER_REJECT;
          }
          if (parent.closest('.rv-player, .rv-context-menu, .rv-word-highlight, .rv-toolbar, .rv-tb-dropdown, .rv-toggle-btn, .rv-simplify-overlay, .rv-magnifier-panel, .rv-doc-skip')) {
            return NodeFilter.FILTER_REJECT;
          }
          if (parent.closest(MATH_SELECTOR)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      });

      let node: Node | null;
      while (node = walker.nextNode()) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element;
          const holder = this.doc.createElement('div');
          holder.appendChild(el.cloneNode(true));
          speakMathIn(holder);
          const spoken = (holder.textContent || '').trim();
          const visible = visibleMathText(el);
          if (!spoken) continue;
          const next = el.nextSibling?.textContent ?? '';
          textNodes.push({ node: visible[0] ?? null, text: ` ${spoken}${/^[.,;:!?)]/.test(next) ? '' : ' '}`, math: visible });
          continue;
        }
        const text = node.textContent || '';
        if (text.trim().length > 0) {
          textNodes.push({ node: node as Text, text });
        }
      }
    }
    return textNodes;
  }

  private buildNodeMap(textNodes: NodeEntry[]): {
    concatenated: string;
    nodeMap: MappedNode[];
  } {
    let concatenated = '';
    const nodeMap: MappedNode[] = [];
    for (const tn of textNodes) {
      nodeMap.push({ node: tn.node, startInConcat: concatenated.length, text: tn.text, math: tn.math });
      concatenated += tn.text;
    }
    return { concatenated, nodeMap };
  }

  private buildLocation(
    nodeMap: MappedNode[],
    sentenceStart: number,
    sentenceLength: number
  ): SentenceLocation {
    const sentenceEnd = sentenceStart + sentenceLength;
    const textNodes: SentenceLocation['textNodes'] = [];
    let hasMath = false;

    for (const nm of nodeMap) {
      const nodeStart = nm.startInConcat;
      const nodeEnd = nodeStart + nm.text.length;
      if (nodeEnd <= sentenceStart) continue;
      if (nodeStart >= sentenceEnd) break;

      // A formula is highlighted whole (its visible glyphs)
      if (nm.math) {
        hasMath = true;
        for (const t of nm.math) textNodes.push({ node: t, offset: 0, length: t.length });
        continue;
      }

      const overlapStart = Math.max(sentenceStart, nodeStart);
      const overlapEnd = Math.min(sentenceEnd, nodeEnd);
      if (!nm.node) continue;
      textNodes.push({
        node: nm.node,
        offset: overlapStart - nodeStart,
        length: overlapEnd - overlapStart
      });
    }

    return {
      sentence: nodeMap.map(n => n.text).join('').substring(sentenceStart, sentenceEnd),
      textNodes,
      totalLength: sentenceLength,
      hasMath,
    };
  }

  private buildLocationFromCollapsed(
    nodeMap: MappedNode[],
    collapsedStart: number,
    collapsedLength: number,
    _collapsedConcat: string,
    originalConcat: string
  ): SentenceLocation | null {
    let collapsedPos = 0;
    let originalPos = 0;

    while (collapsedPos < collapsedStart && originalPos < originalConcat.length) {
      if (/\s/.test(originalConcat[originalPos])) {
        while (originalPos < originalConcat.length - 1 && /\s/.test(originalConcat[originalPos + 1])) {
          originalPos++;
        }
      }
      collapsedPos++;
      originalPos++;
    }

    return this.buildLocation(nodeMap, originalPos, collapsedLength);
  }

  private buildLocationFromStripped(
    nodeMap: MappedNode[],
    originalConcat: string,
    strippedStart: number,
    strippedLength: number
  ): SentenceLocation | null {
    // Find annotation ranges in original text
    const annotationRanges: { start: number; end: number }[] = [];
    const rx = /\[[\w\s]+\]/g;
    let m: RegExpExecArray | null;
    while ((m = rx.exec(originalConcat)) !== null) {
      annotationRanges.push({ start: m.index, end: m.index + m[0].length });
    }

    // Walk original text, skipping annotations + collapsing whitespace
    let strippedPos = 0;
    let lastWasSpace = false;
    let origStart = 0;
    let origEnd = 0;
    let foundStart = false;

    for (let i = 0; i < originalConcat.length && strippedPos <= strippedStart + strippedLength; i++) {
      if (annotationRanges.some(r => i >= r.start && i < r.end)) continue;

      const isSpace = /\s/.test(originalConcat[i]);
      if (isSpace && lastWasSpace) continue;
      lastWasSpace = isSpace;

      if (strippedPos === strippedStart && !foundStart) {
        origStart = i;
        foundStart = true;
      }
      if (strippedPos === strippedStart + strippedLength) {
        origEnd = i;
        break;
      }
      strippedPos++;
    }

    if (!foundStart) return null;
    if (origEnd <= origStart) {
      origEnd = Math.min(origStart + strippedLength + 20, originalConcat.length);
    }
    return this.buildLocation(nodeMap, origStart, origEnd - origStart);
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  removeHighlight(): void {
    this.clearWordHighlight();
  }

  clearAll(): void {
    this.clearWordHighlight();
    this.clearSentenceHighlight();
    this.currentSentence = null;
    this.currentWords = [];
    this.lastSentenceText = '';

    // Remove any orphaned DOM highlights
    this.doc.querySelectorAll('.rv-word-highlight, .rv-sentence-highlight').forEach(el => {
      const parent = el.parentNode;
      if (parent) {
        const text = this.doc.createTextNode(el.textContent || '');
        parent.replaceChild(text, el);
        parent.normalize();
      }
    });
  }

  setAutoScroll(enabled: boolean): void {
    this.autoScroll = enabled;
  }

  destroy(): void {
    this.clearAll();
  }
}

/** Lower-cased letters/digits of `s`, with each one's index in the original string */
function alnumIndex(s: string): { text: string; map: number[] } {
  let text = '';
  const map: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (/[\p{L}\p{N}]/u.test(c)) {
      text += c.toLowerCase();
      map.push(i);
    }
  }
  return { text, map };
}
